/**
 * Question Bank types and validation.
 *
 * DTOs are the server-owned shapes returned by GET/POST /api/questions
 * (author-api). Drafts are the client-composed create payloads, validated by
 * `questionDraftSchema` in the create modal on submit and mirrored in
 * author-api before the create transaction (author-api/src/validation/
 * questionDraft.ts — keep the two in sync; the repos cannot share a package).
 */
import { z } from 'zod';
import type { PublishJobSummary } from './publishJobs';
import type { JSONContent } from '@tiptap/core';
import { isRichTextContentEmpty } from './richText';
import type { QuestionPurpose } from './questionPurpose';
import type {
  TranslationRecordStatus,
  TranslationScopeSummary,
  TranslationType,
} from './translation';

export type QuestionBlockType =
  | 'likert-scale'
  | 'single-choice-answer'
  | 'workplace-color-spectrum'
  | 'categorization'
  | 'freeform-text';

export const QUESTION_BLOCK_TYPES: readonly QuestionBlockType[] = [
  'likert-scale',
  'single-choice-answer',
  'workplace-color-spectrum',
  'categorization',
  'freeform-text',
];

/**
 * Which fields a question locale row may carry, per type. Everything absent is
 * structural and always resolves from the English bank rows — Likert scale
 * labels and Workplace Color Spectrum colour labels included.
 *
 * Mirror of QUESTION_TRANSLATABLE_FIELDS in
 * author-api/src/services/questionTranslatableFields.ts, which is where the
 * server's resolver, translation writer, and source-change detection all read
 * it from. The two repos cannot import from each other, so keep them in step.
 */
export const QUESTION_TRANSLATABLE_FIELDS = {
  'likert-scale': ['displayText'],
  'single-choice-answer': ['displayText', 'options', 'correctAnswerExplanation'],
  'workplace-color-spectrum': ['displayText', 'correctAnswerExplanation'],
  categorization: ['displayText', 'categories', 'items'],
  'freeform-text': ['displayText'],
} as const satisfies Record<QuestionBlockType, readonly QuestionTranslationField[]>;

export type QuestionTranslationField =
  | 'displayText'
  | 'options'
  | 'categories'
  | 'items'
  | 'correctAnswerExplanation';

/**
 * Safe text-only projection of an authorAIQuestionContent payload. Keys in
 * label maps are persisted child ids; structural question data never belongs
 * in this shape.
 */
export interface QuestionTranslationFields {
  displayText?: JSONContent;
  options?: Record<string, string>;
  categories?: Record<string, string>;
  items?: Record<string, string>;
  correctAnswerExplanation?: JSONContent;
}

export interface QuestionTranslationProjection {
  questionDefinitionId: number;
  locale: string;
  fields: QuestionTranslationFields;
  exists: boolean;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
  status: TranslationRecordStatus;
  summary: TranslationScopeSummary;
}

export interface QuestionTranslationPatch {
  fields: QuestionTranslationFields;
}

export type QuestionPublishJobScope = { kind: 'source' } | { kind: 'translation'; locale: string };
/** Compatibility alias for existing Question Bank callers. */
export type PublishJobScope = QuestionPublishJobScope;
export type {
  PublishJobStatus, PublishJobStepOutcome, PublishJobAttemptSummary, PublishJobDto, PublishJobSummary,
} from './publishJobs';

// ── DTOs ──────────────────────────────────────────────────────────────────────

export interface QuestionAnswerOptionDto {
  id: number;
  label: string;
  position: number;
  isTarget: boolean;
  /**
   * Correct-answer explanation (gated Single Choice / Workplace Color
   * Spectrum), present only on the target row; null otherwise.
   */
  correctAnswerExplanation: JSONContent | null;
}

export interface QuestionCategorizeCategoryDto {
  id: number;
  label: string;
  /** Encoded image-library slug (matches what the picker itself hands out), not a raw id. */
  imageLibraryItemId: string | null;
  /** Resolved image URL, for redisplay — null when there's no image or it can't be resolved. */
  imageUrl: string | null;
  emoji: string | null;
}

export interface QuestionCategorizeItemDto {
  id: number;
  label: string;
  correctCategoryId: number;
  /** Encoded image-library slug (matches what the picker itself hands out), not a raw id. */
  imageLibraryItemId: string | null;
  /** Resolved image URL, for redisplay — null when there's no image or it can't be resolved. */
  imageUrl: string | null;
  emoji: string | null;
}

export interface QuestionDefinitionDto {
  questionDefinitionId: number;
  blockType: QuestionBlockType;
  /** Only meaningful for single-choice-answer; false for other types. */
  gated: boolean;
  questionText: JSONContent;
  /** Plain-text projection of questionText for combobox display/search. */
  questionTextPlain: string;
  /**
   * NA-384: the bank's canonical short name for the question, distinct from
   * questionText. Null for a question created before NA-384 shipped, until an
   * author backfills one.
   */
  title: string | null;
  isPublic: boolean;
  /** NULL = Emtrain Standard bank; a value = that client account's private bank. */
  externalAccountId: number | null;
  /** NA-391: derived server-side on every save from the question's configuration. */
  purpose: QuestionPurpose;
  answerOptions: QuestionAnswerOptionDto[];
  categories: QuestionCategorizeCategoryDto[];
  items: QuestionCategorizeItemDto[];
  createdAt: string;
  updatedAt: string;
  /** Set when the question is archived (retired); null while active. */
  retiredAt: string | null;
}

export type QuestionListSort = 'id' | 'questionText' | 'type' | 'public' | 'usedIn' | 'responses' | 'createdAt';
export type QuestionListOrder = 'asc' | 'desc';
export type QuestionListPageSize = 25 | 50 | 100;

export interface QuestionListParams {
  page?: number;
  pageSize?: QuestionListPageSize;
  search?: string;
  sort?: QuestionListSort;
  order?: QuestionListOrder;
  blockType?: QuestionBlockType;
  gated?: boolean;
  includeArchived?: boolean;
  /** Content locale only; filtering and sorting remain English-first. */
  locale?: string;
  /** Scope: omitted/null = Emtrain Standard bank; a value = that client account's own pool only. */
  externalAccountId?: number | null;
}

/** One NA-385 taxonomy tag on a question, with the display strings the tag resolves to. */
export interface QuestionTaxonomyTag {
  taxonomyId: number;
  taxonomyName: string;
  /** "Culture Skills", "Risk Areas", "Leadership Skills". */
  displayName: string;
  parentLevelName: string;
  childLevelName: string;
  labelId: number;
  parentLabel: string;
  childLabel: string;
}

/** One concept a question is mapped to (`authorAIQuestionConceptMappings`), as the list rows carry it. */
export interface QuestionConceptRef {
  id: number;
  name: string;
}

export interface QuestionListItem extends QuestionDefinitionDto {
  usageCount: number;
  /** The question's response total from the learner summary; 0 when it has none. */
  responseCount: number;
  /** Empty when the question is untagged. */
  taxonomyTags: QuestionTaxonomyTag[];
  /** The question's concepts by name; empty when it is mapped to none. */
  concepts: QuestionConceptRef[];
  publishJob?: PublishJobSummary | null;
  /** Present on locale-aware list responses; existing English fields remain unchanged. */
  translation?: QuestionTranslationProjection;
  /** English source reference for non-English translation mode. */
  englishSource?: QuestionDefinitionDto;
}

export interface QuestionListResult {
  items: QuestionListItem[];
  totalCount: number;
}

/** One question type and purpose pair the bank holds in a scope, with its live question count. */
export interface QuestionTypePurposeCount {
  blockType: QuestionBlockType;
  purpose: QuestionPurpose;
  questionCount: number;
}

export interface QuestionBrowseCategory {
  id: number;
  name: string;
}

export interface QuestionBrowseConcept {
  id: number;
  name: string;
  categoryId: number;
  /** Live questions in scope mapped to the concept; 0 when none. */
  questionCount: number;
}

export interface QuestionBrowseTaxonomyLabel {
  labelId: number;
  label: string;
  questionCount: number;
}

export interface QuestionBrowseTaxonomyParent {
  label: string;
  questionCount: number;
  children: QuestionBrowseTaxonomyLabel[];
}

export interface QuestionBrowseTaxonomy {
  taxonomyId: number;
  /** The stored name: "Culture", "Risk", "Leadership". */
  name: string;
  /** "Culture Skills", "Risk Areas", "Leadership Skills". */
  displayName: string;
  parentLevelName: string;
  childLevelName: string;
  questionCount: number;
  parents: QuestionBrowseTaxonomyParent[];
}

/** GET /api/questions/facets: what the Question Bank page builds its controls from, for one scope. */
export interface QuestionBrowseFacets {
  typePurposes: QuestionTypePurposeCount[];
  /** Categories in priority order. */
  categories: QuestionBrowseCategory[];
  concepts: QuestionBrowseConcept[];
  taxonomies: QuestionBrowseTaxonomy[];
}

// ── Drafts ────────────────────────────────────────────────────────────────────

const richTextSchema = z
  .object({ type: z.string(), content: z.array(z.any()).optional() })
  .passthrough();

const requiredQuestionTextSchema = richTextSchema.refine(
  (value) => !isRichTextContentEmpty(value as JSONContent),
  { message: 'Question text is required' },
);

const draftOptionSchema = z.object({
  // Present when this option is an existing, persisted row being edited —
  // the update endpoint matches on it to edit in place instead of deleting
  // and recreating (see question-bank-storage's stable-id requirement).
  // Absent for a newly-added option, which the server creates fresh.
  id: z.number().int().positive().optional(),
  label: z.string(),
  isTarget: z.boolean(),
});

const likertQuestionDraftObject = z.object({
  blockType: z.literal('likert-scale'),
  questionText: requiredQuestionTextSchema,
  // NA-384: the bank's canonical short name. Optional on both create and update.
  title: z.string().optional(),
  isPublic: z.boolean(),
  // NULL/omitted = Emtrain Standard bank; a value = that client account's
  // private bank. Resolved client-side from the active Content Context.
  // Immutable after creation — the update endpoint ignores this field.
  externalAccountId: z.number().int().positive().nullish(),
  options: z.array(draftOptionSchema).min(1, 'At least one answer option is required'),
});

const singleChoiceQuestionDraftObject = z.object({
  blockType: z.literal('single-choice-answer'),
  gated: z.boolean(),
  questionText: requiredQuestionTextSchema,
  title: z.string().optional(),
  isPublic: z.boolean(),
  // NULL/omitted = Emtrain Standard bank; a value = that client account's
  // private bank. Resolved client-side from the active Content Context.
  // Immutable after creation — the update endpoint ignores this field.
  externalAccountId: z.number().int().positive().nullish(),
  options: z.array(draftOptionSchema).min(1, 'At least one answer option is required'),
  // Top-level for the single modal field; server writes it onto the target
  // option row (gated only).
  correctAnswerExplanation: richTextSchema.optional(),
});

const workplaceColorSpectrumQuestionDraftObject = z.object({
  blockType: z.literal('workplace-color-spectrum'),
  questionText: requiredQuestionTextSchema,
  title: z.string().optional(),
  isPublic: z.boolean(),
  // NULL/omitted = Emtrain Standard bank; a value = that client account's
  // private bank. Resolved client-side from the active Content Context.
  // Immutable after creation — the update endpoint ignores this field.
  externalAccountId: z.number().int().positive().nullish(),
  options: z.array(draftOptionSchema).min(1, 'At least one answer option is required'),
  correctAnswerExplanation: richTextSchema.optional(),
});

// imageLibraryItemId is whatever the image picker actually hands out — the
// encoded sqids slug (the normal case), not a raw number. author-api resolves
// either shape server-side before writing it to the imageId column (mirror:
// author-api/src/validation/questionDraft.ts).
const draftImageLibraryItemIdSchema = z.union([z.number(), z.string()]).nullish();

const draftCategorySchema = z.object({
  // Present when this category is an existing, persisted row being edited —
  // see draftOptionSchema's `id` for the same stable-id contract.
  id: z.number().int().positive().optional(),
  tempId: z.string().min(1),
  label: z.string(),
  emoji: z.string().nullish(),
  imageLibraryItemId: draftImageLibraryItemIdSchema,
});

const draftItemSchema = z.object({
  // Present when this item is an existing, persisted row being edited — see
  // draftOptionSchema's `id` for the same stable-id contract.
  id: z.number().int().positive().optional(),
  label: z.string(),
  correctCategoryTempId: z.string().min(1),
  emoji: z.string().nullish(),
  imageLibraryItemId: draftImageLibraryItemIdSchema,
});

const categorizationQuestionDraftObject = z.object({
  blockType: z.literal('categorization'),
  questionText: requiredQuestionTextSchema,
  title: z.string().optional(),
  isPublic: z.boolean(),
  // NULL/omitted = Emtrain Standard bank; a value = that client account's
  // private bank. Resolved client-side from the active Content Context.
  // Immutable after creation — the update endpoint ignores this field.
  externalAccountId: z.number().int().positive().nullish(),
  categories: z.array(draftCategorySchema).min(1, 'At least one category is required'),
  items: z.array(draftItemSchema).min(1, 'At least one item is required'),
});

// Freeform Text is the first question type with no answer options/categories/
// items at all. `purpose` is bank-level metadata restricted to Sentiment or
// Reflection here (NA-391 may later widen it for other types). The
// 500-character response limit is a fixed constant, never a draft field.
const freeformTextQuestionDraftObject = z.object({
  blockType: z.literal('freeform-text'),
  questionText: requiredQuestionTextSchema,
  title: z.string().optional(),
  isPublic: z.boolean(),
  // NULL/omitted = Emtrain Standard bank; a value = that client account's
  // private bank. Resolved client-side from the active Content Context.
  // Immutable after creation — the update endpoint ignores this field.
  externalAccountId: z.number().int().positive().nullish(),
  purpose: z.enum(['Sentiment', 'Reflection']).default('Sentiment'),
});

// zod discriminated unions reject ZodEffects members, so per-type
// cross-field rules live in a superRefine over the whole union.
const questionDraftUnion = z.discriminatedUnion('blockType', [
  likertQuestionDraftObject,
  singleChoiceQuestionDraftObject,
  workplaceColorSpectrumQuestionDraftObject,
  categorizationQuestionDraftObject,
  freeformTextQuestionDraftObject,
]);

export const questionDraftSchema = questionDraftUnion.superRefine((draft, ctx) => {
  if (draft.blockType === 'single-choice-answer' && draft.gated
    && !draft.options.some((option) => option.isTarget)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'A gated question requires a correct answer',
    });
  }
  if (draft.blockType === 'workplace-color-spectrum'
    && !draft.options.some((option) => option.isTarget)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'A correct answer is required',
    });
  }
  if (draft.blockType === 'categorization') {
    const categoryTempIds = new Set(draft.categories.map((category) => category.tempId));
    draft.items.forEach((item, index) => {
      if (!categoryTempIds.has(item.correctCategoryTempId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'correctCategoryTempId'],
          message: 'Item references an unknown category',
        });
      }
    });
  }
});

export type LikertQuestionDraft = z.infer<typeof likertQuestionDraftObject>;
export type SingleChoiceQuestionDraft = z.infer<typeof singleChoiceQuestionDraftObject>;
export type WorkplaceColorSpectrumQuestionDraft = z.infer<typeof workplaceColorSpectrumQuestionDraftObject>;
export type CategorizationQuestionDraft = z.infer<typeof categorizationQuestionDraftObject>;
export type FreeformTextQuestionDraft = z.infer<typeof freeformTextQuestionDraftObject>;
export type QuestionDraft = z.infer<typeof questionDraftSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Whether a question block has renderable question content. Blocks without it
 * render learner-side as title-only and must not gate progress.
 *
 * Single Choice and Categorization need their structural content (options, or
 * categories + items) to render at all, so an empty one falls back. Likert and
 * Workplace Color Spectrum always render a complete interaction from their
 * scale / colour defaults — the description is an optional prompt, not the
 * content — so they never fall back to title-only.
 */
export function questionBlockHasContent(block: {
  type: string;
  options?: unknown[];
  categories?: unknown[];
  items?: unknown[];
}): boolean {
  switch (block.type) {
    case 'single-choice-answer':
      return (block.options?.length ?? 0) >= 1;
    case 'categorization':
      return (block.categories?.length ?? 0) >= 1 && (block.items?.length ?? 0) >= 1;
    default:
      return true;
  }
}
